import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import LandingPage from "./pages/LandingPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import SetPassword from "./pages/SetPassword";
import { api } from "./api";

function App() {
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const loadActive = async () => {
    try {
      const s = await api.getActiveSite();
      setActiveSite(s);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async () => {
    try {
      const list = await api.listSites();
      setSites(list);
    } catch (e) {
      console.error(e);
    }
  };

  const loadMe = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      setAuthChecked(true);
      return;
    }
    try {
      const me = await api.me();
      setUser(me);
      await loadSites();
    } catch (e) {
      localStorage.removeItem("admin_token");
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  };

  useEffect(() => {
    loadActive();
    loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (email, password) => {
    try {
      const data = await api.login(email, password);
      if (data.twofa_required) {
        return { twofa: true, ticket: data.ticket };
      }
      localStorage.setItem("admin_token", data.access_token);
      setUser(data.user);
      await loadSites();
      return { ok: true };
    } catch (e) {
      return { ok: false };
    }
  };

  const handleVerify2fa = async (ticket, code) => {
    try {
      const data = await api.verify2fa(ticket, code);
      localStorage.setItem("admin_token", data.access_token);
      setUser(data.user);
      await loadSites();
      return { ok: true };
    } catch (e) {
      return { ok: false };
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setUser(null);
  };

  const refresh = async () => {
    await Promise.all([loadSites(), loadActive()]);
  };

  const PERM_MSG = "You don't have permission for this. Please contact your admin to turn on this feature.";
  const errMsg = (e, fallback) => (e?.response?.status === 403 ? PERM_MSG : fallback);

  const onCreate = async (payload) => {
    try {
      await api.createSite(payload);
      await refresh();
      toast.success("Site added");
    } catch (e) {
      toast.error(errMsg(e, "Failed to add site"));
    }
  };
  const onUpdate = async (id, payload) => {
    try {
      await api.updateSite(id, payload);
      await refresh();
      toast.success("Site updated");
    } catch (e) {
      toast.error(errMsg(e, "Failed to update site"));
    }
  };
  const onDelete = async (id) => {
    try {
      await api.deleteSite(id);
      await refresh();
      toast.success("Site deleted");
    } catch (e) {
      toast.error(errMsg(e, "Failed to delete site"));
    }
  };
  const onSetActive = async (id) => {
    try {
      await api.activateSite(id);
      await refresh();
      toast.success("Active site updated");
    } catch (e) {
      toast.error(errMsg(e, "Failed to set active site"));
    }
  };

  const authed = !!user;

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage site={activeSite} loading={loading} />} />
          <Route
            path="/admin/login"
            element={authed ? <Navigate to="/admin" replace /> : <AdminLogin onLogin={handleLogin} onVerify2fa={handleVerify2fa} />}
          />
          <Route path="/admin/reset" element={<SetPassword mode="reset" />} />
          <Route path="/admin/invite" element={<SetPassword mode="invite" onAuthed={loadMe} />} />
          <Route
            path="/admin"
            element={
              !authChecked ? (
                <div className="dot-grid min-h-screen flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full border-4 border-[#25D366]/30 border-t-[#25D366] animate-spin" />
                </div>
              ) : authed ? (
                <AdminDashboard
                  sites={sites}
                  user={user}
                  onCreate={onCreate}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                  onSetActive={onSetActive}
                  onLogout={handleLogout}
                  onUserRefresh={loadMe}
                />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;



